import type {
  AgentOrchestratorPlanStep,
  AgentOrchestratorStepResult,
  OntologyAgentEvent
} from '@atbash-labs/ontology-agent-wire'

import {
  invalidateChangedOntologyResourceCaches,
  invalidateCompletedToolCaches
} from '../api/cache-coherence/orchestrator-cache-invalidation'
import {
  addTabAgentAction,
  addTabCustomAgentBulkBatch,
  addTabCustomAgentRun,
  addTabIngestWait,
  appendInterStepTextDelta,
  appendSubagentProgressDelta,
  appendTabFinalSummaryDelta,
  appendTabStepDelta,
  commitSubagentProgressPending,
  discardSubagentProgressPending,
  hydrateAgentActionProposalsFromToolOutput,
  removeInFlightAgentActionsForPlan,
  removeTabAgentActionsForParent,
  removeTabIngestWait,
  setTabFinalSummary,
  setTabModelFallback,
  setTabContextCompacted,
  setTabPlan,
  updateAgentOrchestratorConversationTitle,
  upsertTabStepResult,
  upsertTabToolCall,
  type ConversationRun,
  type LiveOrchestratorToolCall,
  type RunStatus
} from '../slices/agent-orchestrator-slice'
import type { AppDispatch } from '../store'
import { getAgentActionLifecycleByToolName } from './agent-actions/lifecycle-registry'
import { nextLiveToolBatchId } from './tool-call-batches'
// Side-effect import: registers the built-in agent-action capabilities
// (cypher_write today) so routing/resume/panel can look them up by kind.
// Lives here so any caller of the shared event router (the orchestrator run
// thunks AND the factory-monitoring run thunk) gets the registrations.
import './agent-actions/cypher-write-capability'
import './agent-actions/servicenow-incident-capability'
import './agent-actions/create-branch-capability'
import './agent-actions/custom-agent-run-capability'
import './agent-actions/writeback-tool-capability'
import './agent-actions/custom-agent-config-write-capability'
import './agent-actions/tag-write-capability'
import './agent-actions/tag-write-policy-capability'
import './agent-actions/sightings-write-capability'
import './agent-actions/tag-historization-capability'

/**
 * Shared SSE event router for orchestrator-shaped agent runs.
 *
 * Both the chat orchestrator (`run-agent-orchestrator.ts`) and the
 * factory-monitoring planner (`run-factory-monitoring-agent.ts`) stream the
 * same `OntologyAgentEvent` envelope (plan → step → tool-call → final summary)
 * into the orchestrator slice's tab state. This module owns that routing so
 * neither run thunk has to import the other's internals.
 */

/**
 * Tool name kept in sync with `WAIT_FOR_INGEST_JOB_TOOL_NAME` in
 * `@atbash-labs/ontology-agents`. Duplicated here as a literal so the
 * UI state package stays free of a runtime dependency on the agents
 * package — the agent's exported constant is just `'wait_for_ingest_job'`.
 */
const WAIT_FOR_INGEST_JOB_TOOL_NAME = 'wait_for_ingest_job'

function parseTimestampMs(timestamp: string | undefined): number | null {
  if (typeof timestamp !== 'string') return null
  const ms = Date.parse(timestamp)
  return Number.isFinite(ms) ? ms : null
}

function readJobIdFromInput(input: unknown): string {
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const value = (input as Record<string, unknown>).job_id
    if (typeof value === 'string') return value
  }
  return ''
}

/**
 * Build the per-run event handler scoped to a specific conversation. The
 * `conversationId` closure is the critical difference from the old
 * singleton handler — every dispatch routes to the named tab so background
 * tabs receive their own events even when the user is looking at a
 * different tab.
 *
 * The orchestrator emits these shapes the UI cares about:
 *
 *   - `agent_orchestrator_conversation_started`: arrives ONCE before the
 *     planner runs. With multi-tab the slice already knows the conversation
 *     id (set when the tab was opened), so this event is now informational
 *     — we no longer flip activeTabId on it (decision #7 / open-or-focus).
 *   - `agent_orchestrator_conversation_title`: first-turn Nova 2 Lite title
 *     replacing the truncated-instruction placeholder on the rail / tabs.
 *   - `agent_orchestrator_plan_created`: commits the plan to the tab's
 *     run state so the plan card can render before any subagent runs.
 *   - `agent_orchestrator_step_start` / `..._step_complete`: per-step
 *     lifecycle. `start` makes the row visible (with no result yet);
 *     `complete` populates the row's summary + completed flag.
 *   - `agent_orchestrator_step_text_delta`: incremental token-by-token
 *     prose for a `text`-kind step's summary.
 *   - `agent_orchestrator_finalized`: ends the run with the synthesized
 *     summary.
 *   - `agent_orchestrator_final_summary_delta`: incremental token-by-token
 *     prose for the final wrap-up paragraph.
 *   - `tool_call_start` / `tool_call_complete` carrying `parentStepIndex`:
 *     subagent tool calls attributed to a plan step.
 */
export function createOrchestratorEventHandler(dispatch: AppDispatch, conversationId: string) {
  let plan: AgentOrchestratorPlanStep[] = []
  const stepResults: Record<number, AgentOrchestratorStepResult> = {}
  /** Completed tool calls attributed to a plan step — persisted on the synthetic turn. */
  const toolCalls: LiveOrchestratorToolCall[] = []
  let nextBatchSeq = 0
  let finalSummary: string | null = null
  let observedError: string | null = null
  let observedConversationStarted = false

  const handle = (event: OntologyAgentEvent): void => {
    switch (event.type) {
      case 'agent_orchestrator_conversation_started':
        // The slice's tab was already opened by the caller (pre-create
        // path or supplied-id path) — this event is purely informational
        // confirmation. Used to gate the "did the server actually accept
        // this run?" check in the post-stream block (synthetic turn is
        // only appended when this event arrived).
        observedConversationStarted = true
        break

      case 'agent_orchestrator_conversation_title':
        dispatch(
          updateAgentOrchestratorConversationTitle({
            id: event.conversationId,
            title: event.title
          })
        )
        break

      case 'custom_agent_run_dispatched':
        dispatch(
          addTabCustomAgentRun({
            id: event.conversationId,
            run: {
              runId: event.runId,
              ...(event.configId ? { configId: event.configId } : {}),
              name: event.name,
              ...(event.parameters ? { parameters: event.parameters } : {}),
              ...(event.kind ? { kind: event.kind } : {}),
              ...(event.parentRunId ? { parentRunId: event.parentRunId } : {})
            }
          })
        )
        break

      case 'custom_agent_bulk_batch_dispatched':
        dispatch(
          addTabCustomAgentBulkBatch({
            id: event.conversationId,
            batch: {
              batchId: event.batchId,
              name: event.name,
              itemCount: event.itemCount
            }
          })
        )
        break

      case 'agent_orchestrator_plan_created':
        plan = event.steps
        dispatch(setTabPlan({ id: conversationId, plan: event.steps }))
        break

      case 'agent_orchestrator_step_start': {
        const prev = stepResults[event.stepIndex]
        const partial: AgentOrchestratorStepResult = {
          stepIndex: event.stepIndex,
          agent: event.agent,
          objective: event.objective,
          summary: prev?.summary ?? '',
          completed: false
        }
        stepResults[event.stepIndex] = partial
        dispatch(upsertTabStepResult({ id: conversationId, result: partial }))
        break
      }

      case 'agent_orchestrator_step_text_delta': {
        const prev = stepResults[event.stepIndex]
        if (prev) {
          stepResults[event.stepIndex] = {
            ...prev,
            summary: prev.summary + event.textDelta
          }
        } else {
          stepResults[event.stepIndex] = {
            stepIndex: event.stepIndex,
            agent: 'text',
            objective: '',
            summary: event.textDelta,
            completed: false
          }
        }
        dispatch(
          appendTabStepDelta({
            id: conversationId,
            stepIndex: event.stepIndex,
            delta: event.textDelta
          })
        )
        break
      }

      case 'agent_orchestrator_step_complete': {
        const prev = stepResults[event.stepIndex]
        const merged: AgentOrchestratorStepResult = {
          stepIndex: event.stepIndex,
          agent: prev?.agent ?? 'text',
          objective: prev?.objective ?? '',
          summary: event.summary,
          completed: event.completed,
          rawResponse: event.rawResponse
        }
        stepResults[event.stepIndex] = merged
        dispatch(upsertTabStepResult({ id: conversationId, result: merged }))
        break
      }

      case 'agent_orchestrator_final_summary_delta':
        finalSummary = (finalSummary ?? '') + event.textDelta
        dispatch(
          appendTabFinalSummaryDelta({
            id: conversationId,
            delta: event.textDelta
          })
        )
        break

      case 'agent_orchestrator_inter_step_delta':
        dispatch(
          appendInterStepTextDelta({
            id: conversationId,
            stepIndex: event.stepIndex,
            delta: event.textDelta
          })
        )
        break

      case 'agent_orchestrator_subagent_progress_delta':
        dispatch(
          appendSubagentProgressDelta({
            id: conversationId,
            stepIndex: event.stepIndex,
            delta: event.textDelta
          })
        )
        break

      case 'agent_orchestrator_subagent_progress_commit':
        dispatch(
          commitSubagentProgressPending({
            id: conversationId,
            stepIndex: event.stepIndex
          })
        )
        break

      case 'agent_orchestrator_subagent_progress_discard':
        dispatch(
          discardSubagentProgressPending({
            id: conversationId,
            stepIndex: event.stepIndex
          })
        )
        break

      case 'agent_orchestrator_finalized':
        finalSummary = event.summary
        dispatch(setTabFinalSummary({ id: conversationId, summary: event.summary }))
        break

      case 'context_compacted':
        dispatch(
          setTabContextCompacted({
            id: conversationId,
            compacted: {
              provider: event.provider,
              model: event.model,
              mode: event.mode,
              reason: event.reason,
              ...(event.inputTokens === undefined ? {} : { inputTokens: event.inputTokens }),
              ...(event.contextWindowTokens === undefined
                ? {}
                : { contextWindowTokens: event.contextWindowTokens })
            }
          })
        )
        break

      case 'model_fallback':
        dispatch(
          setTabModelFallback({
            id: conversationId,
            fallback: {
              requestedProvider: event.requestedProvider,
              requestedModel: event.requestedModel,
              provider: event.provider,
              model: event.model,
              reason: event.reason,
              notify: event.notify
            }
          })
        )
        break

      case 'tool_call_start':
        if (event.parentStepIndex === undefined) break
        {
          const existingIdx = toolCalls.findIndex((tc) => tc.id === event.toolUseId)
          const existing = existingIdx >= 0 ? toolCalls[existingIdx] : undefined
          // Prefer the server-stamped turn id so serial start/complete
          // streams of one assistant turn still group while in flight.
          // Fall back to overlap when an older emitter omitted it.
          const batchId =
            event.batchId ??
            existing?.batchId ??
            nextLiveToolBatchId(toolCalls, event.parentStepIndex, () => `batch-${nextBatchSeq++}`)
          const running: LiveOrchestratorToolCall = {
            id: event.toolUseId,
            name: event.toolName,
            input: event.input,
            output: undefined,
            parentStepIndex: event.parentStepIndex,
            agentKind: event.subagentKind,
            innerStepIndex: event.stepIndex,
            status: 'running',
            batchId
          }
          if (existingIdx >= 0) toolCalls[existingIdx] = running
          else toolCalls.push(running)
          dispatch(upsertTabToolCall({ id: conversationId, toolCall: running }))
        }
        if (event.toolName === WAIT_FOR_INGEST_JOB_TOOL_NAME) {
          const startedAtMs = parseTimestampMs(event.timestamp) ?? Date.now()
          const jobId = readJobIdFromInput(event.input)
          dispatch(
            addTabIngestWait({
              id: conversationId,
              waitId: event.toolUseId,
              jobId,
              startedAtMs
            })
          )
        } else {
          // Agent-action approval tools (cypher writes today) are routed
          // by tool name through the capability registry, so adding a new
          // permission-gated tool needs no change to this SSE loop.
          const capability = getAgentActionLifecycleByToolName(event.toolName)
          if (capability) {
            const startedAtMs = parseTimestampMs(event.timestamp) ?? Date.now()
            const entries = capability.parseToolStart(event.toolUseId, event.input, startedAtMs)
            for (const entry of entries) {
              dispatch(
                addTabAgentAction({
                  id: conversationId,
                  actionId: entry.id,
                  proposal: entry.proposal
                })
              )
            }
            capability.scheduleHydratePending?.(dispatch, conversationId)
          }
        }
        break

      case 'tool_call_complete':
        // Cache coherence is independent from whether the call can be
        // attributed to a visible orchestrator plan step.
        invalidateCompletedToolCaches(dispatch, event.toolCall.name)
        if (event.parentStepIndex === undefined) break
        {
          const existingIdx = toolCalls.findIndex((tc) => tc.id === event.toolCall.id)
          const existing = existingIdx >= 0 ? toolCalls[existingIdx] : undefined
          const completed: LiveOrchestratorToolCall = {
            ...event.toolCall,
            parentStepIndex: event.parentStepIndex,
            agentKind: event.subagentKind ?? existing?.agentKind,
            innerStepIndex: event.stepIndex,
            status: 'completed',
            batchId: existing?.batchId
          }
          if (existingIdx >= 0) toolCalls[existingIdx] = completed
          else toolCalls.push(completed)
          dispatch(upsertTabToolCall({ id: conversationId, toolCall: completed }))
        }
        if (event.toolCall.name === WAIT_FOR_INGEST_JOB_TOOL_NAME) {
          dispatch(
            removeTabIngestWait({
              id: conversationId,
              waitId: event.toolCall.id
            })
          )
        } else {
          const capability = getAgentActionLifecycleByToolName(event.toolCall.name)
          if (capability) {
            // Blocking propose tools complete AFTER the user's decisions:
            // a settled output means the outcomes were delivered inside
            // this turn, so the plan's cards retire here — the agent's
            // continuation text is the record. Removal is keyed by both
            // planId and parent tool-use id so cards whose planId never
            // hydrated (e.g. the pending poll lost a race) still vacate.
            const settled = capability.parseSettledToolComplete?.(event.toolCall.output)
            if (settled?.kind === 'retire') {
              dispatch(removeInFlightAgentActionsForPlan({ planId: settled.planId }))
              dispatch(
                removeTabAgentActionsForParent({
                  id: conversationId,
                  parentId: event.toolCall.id
                })
              )
            } else if (settled?.kind === 'keep') {
              // Server-side wait timed out — rows are still awaiting_user,
              // the cards stay actionable, and the fallback resume rail
              // owns outcome delivery from here.
            } else {
              // Async-suspend rail (non-blocking ingest proposals, or a
              // not-yet-upgraded server): do NOT clear the cards on
              // success. Hydrate the canonical `planId` + `writeId` from
              // the propose tool's synchronous output so the per-plan
              // completion check can group rows and fire the fallback
              // resume turn once every card settles.
              const hydrate = capability.parseToolComplete(event.toolCall.id, event.toolCall.output)
              if (hydrate) {
                dispatch(hydrateAgentActionProposalsFromToolOutput(hydrate))
              } else if (event.toolCall.output) {
                // Validation / persistence failure — clear the cards
                // because nothing was persisted; the agent will surface
                // the validation error and the user has no row to act on.
                dispatch(
                  removeTabAgentActionsForParent({
                    id: conversationId,
                    parentId: event.toolCall.id
                  })
                )
              }
            }
          }
        }
        break

      case 'ontology_resource_changed':
        invalidateChangedOntologyResourceCaches(dispatch, event.resource)
        break

      case 'error':
        if (observedError === null) observedError = event.message
        break

      // Events the orchestrator UI doesn't need at the top level.
      case 'agent_orchestrator_user_guide_read':
      case 'pipeline_start':
      case 'orchestrator_turn_start':
      case 'final_answer':
      case 'heartbeat':
      case 'chat_wakeup_complete':
        break
    }
  }

  return {
    handle,
    getObservedError: (): string | null => observedError,
    /** True once `agent_orchestrator_conversation_started` arrived — gates the synthetic-turn append. */
    didStart: (): boolean => observedConversationStarted,
    /**
     * Synthesize the just-completed orchestrator run from accumulated live
     * state. The `id` and `started_at`/`finished_at` are placeholders (the
     * proxy assigns canonical values server-side); the conversation list
     * refetch + active conversation refetch will replace this row on the
     * next round trip.
     *
     * Pass `status: 'aborted'` for a run the user stopped — the partial
     * plan and step results are worth keeping, and the server persists
     * the same partial under the same terminal state.
     */
    buildSyntheticTurn: (
      instruction: string,
      options?: { status?: RunStatus }
    ): ConversationRun => {
      const now = new Date().toISOString()
      return {
        id: `pending-${Date.now()}`,
        conversation_id: conversationId,
        parent_run_id: null,
        agent_kind: 'agent_orchestrator',
        position: 0,
        prompt: instruction,
        status: options?.status ?? 'completed',
        raw_response: {
          plan,
          step_results: Object.values(stepResults).sort((a, b) => a.stepIndex - b.stepIndex),
          final_summary: finalSummary ?? '',
          // Direct-dispatch modes have no child runs; keep tool calls here so
          // the debug task list survives live→past before the server refetch.
          tool_calls: toolCalls.map((tc) => ({
            id: tc.id,
            name: tc.name,
            input: tc.input,
            output: tc.output,
            parentStepIndex: tc.parentStepIndex,
            agentKind: tc.agentKind,
            status: tc.status,
            batchId: tc.batchId
          }))
        },
        started_at: now,
        finished_at: now,
        messages: [],
        children: []
      }
    }
  }
}
